import { useState, useEffect, useRef, useCallback } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Stethoscope, Heart, Save, Plus, X, AlertCircle, ClipboardList, Mic, MicOff, Loader2, FlaskConical, Radiation, Scissors, AlertTriangle, FileText, Download, Printer, Search, MessageCircle, Upload, Image, File, Trash2, Store, ShoppingCart, CalendarClock, Zap, Target, Send } from 'lucide-react';
import { toast } from 'sonner';
import { openPrescriptionPrint, downloadPrescriptionPdf, sharePrescriptionWhatsApp } from '../components/PrescriptionTemplate';

// Static ICD-10 lookup for standard quick-diagnosis macros (additive compliance field only - never alters existing note text)
const ICD10_MAP = {
  'viral fever': 'A94',
  'hypertension': 'I10',
  'acute cough': 'R05',
  'type 2 diabetes mellitus': 'E11.9',
  'acute gastritis': 'K29.70',
  'allergic rhinitis': 'J30.9',
};
const getIcd10Code = (label) => ICD10_MAP[(label || '').trim().toLowerCase()] || null;

// Quick Diagnosis Macro chips - 1-click condition templates
const DIAGNOSIS_MACROS = [
  { label: 'Viral Fever', diagnosis: 'Viral Fever', assessment: 'Acute viral febrile illness, likely self-limiting.', plan: 'Antipyretics, hydration, rest. Review in 3 days if fever persists.' },
  { label: 'Hypertension', diagnosis: 'Hypertension', assessment: 'Elevated blood pressure, essential hypertension suspected.', plan: 'Lifestyle modification, continue/start antihypertensive, recheck BP in 1 week.' },
  { label: 'Acute Cough', diagnosis: 'Acute Cough', assessment: 'Acute upper respiratory tract infection with cough.', plan: 'Cough syrup, warm fluids, avoid cold exposure. Review if persists beyond 7 days.' },
  { label: 'Type 2 Diabetes', diagnosis: 'Type 2 Diabetes Mellitus', assessment: 'Known/suspected Type 2 Diabetes Mellitus.', plan: 'Continue/initiate oral hypoglycemics, dietary counseling, recheck sugars.' },
  { label: 'Acute Gastritis', diagnosis: 'Acute Gastritis', assessment: 'Acute gastritis with epigastric discomfort.', plan: 'Antacids/PPI, avoid spicy food, bland diet.' },
  { label: 'Allergic Rhinitis', diagnosis: 'Allergic Rhinitis', assessment: 'Allergic rhinitis with nasal symptoms.', plan: 'Antihistamines, avoid allergens, nasal saline rinse.' },
];

const FOLLOWUP_PRESETS = [
  { label: '3 days', days: 3 }, { label: '1 week', days: 7 }, { label: '2 weeks', days: 14 }, { label: '1 month', days: 30 },
];

// 1-click Health Target suggestions per diagnosis - merged with sensible base defaults
const HEALTH_TARGET_RULES = {
  'Hypertension': { daily_steps: 8000, daily_water_glasses: 8, restrictions: 'Low sodium diet' },
  'Type 2 Diabetes Mellitus': { daily_calories_intake: 1800, daily_steps: 10000, restrictions: 'No added sugar, low carb diet' },
  'Acute Gastritis': { restrictions: 'Avoid spicy/oily food, bland diet' },
  'Viral Fever': { daily_water_glasses: 10, sleep_hours: 9 },
  'Acute Cough': { daily_water_glasses: 9 },
  'Allergic Rhinitis': { restrictions: 'Avoid known allergens/dust' },
};

function VoiceInputButton({ onTranscription, fieldName }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const toggle = useCallback(async () => {
    if (recording) {
      recorderRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'recording.webm');
          const res = await API.post('/ai-scribe/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          onTranscription(res.data.transcription);
          toast.success(`Voice input added to ${fieldName}`);
        } catch (err) { toast.error('Transcription failed'); }
        finally { setTranscribing(false); }
      };
      recorder.start(1000);
      setRecording(true);
    } catch (err) { toast.error('Microphone access denied'); }
  }, [recording, onTranscription, fieldName]);

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={transcribing}
      data-testid={`voice-btn-${fieldName}`}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all ${
        recording ? 'bg-red-500 text-white animate-pulse' : transcribing ? 'bg-slate-200 text-slate-400' : 'bg-slate-100 text-slate-500 hover:bg-teal-100 hover:text-teal-700'
      }`}
      title={recording ? 'Stop recording' : 'Voice input'}
    >
      {transcribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : recording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function ClinicalVisitPage() {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [visits, setVisits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [vitals, setVitals] = useState({ bp_systolic: '', bp_diastolic: '', pulse: '', temperature: '', weight: '', height: '', spo2: '' });
  const [soap, setSoap] = useState({ subjective: '', objective: '', assessment: '', plan: '' });
  const [diagnosis, setDiagnosis] = useState([]);
  const [diagInput, setDiagInput] = useState('');
  const [medications, setMedications] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [radiology, setRadiology] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [labOrders, setLabOrders] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [clinicSettings, setClinicSettings] = useState(null);
  
  // Price catalog and suggestions
  const [prices, setPrices] = useState([]);
  const [medSuggestions, setMedSuggestions] = useState({});
  const [testSuggestions, setTestSuggestions] = useState({});
  const [radSuggestions, setRadSuggestions] = useState({});
  const [treatSuggestions, setTreatSuggestions] = useState({});
  
  // Drug interactions
  const [drugWarnings, setDrugWarnings] = useState([]);
  const [checkingInteractions, setCheckingInteractions] = useState(false);
  
  // AI Scribe sessions
  const [scribeSessions, setScribeSessions] = useState([]);
  const [scribeDialogOpen, setScribeDialogOpen] = useState(false);
  const [importingScribe, setImportingScribe] = useState(false);
  const [kioskData, setKioskData] = useState(null);
  const [loadingKiosk, setLoadingKiosk] = useState(false);
  
  // Clinical visit attachments
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Marketplace product search state - Lab Tests only
  const [marketplaceLabSearch, setMarketplaceLabSearch] = useState('');
  const [marketplaceLabProducts, setMarketplaceLabProducts] = useState([]);
  const [searchingLabMarketplace, setSearchingLabMarketplace] = useState(false);
  const [showLabMarketplaceDropdown, setShowLabMarketplaceDropdown] = useState(false);

  // Inline AI Scribe (recorded directly inside the Consultation Hub, no page redirect)
  const [activeClinicalTab, setActiveClinicalTab] = useState('vitals');
  const [showScribeRecorder, setShowScribeRecorder] = useState(false);
  const [scribeRecording, setScribeRecording] = useState(false);
  const [scribeTranscribing, setScribeTranscribing] = useState(false);
  const [scribeGenerating, setScribeGenerating] = useState(false);
  const [scribeTranscription, setScribeTranscription] = useState('');
  const scribeMediaRecorderRef = useRef(null);
  const scribeChunksRef = useRef([]);
  const scribePanelRef = useRef(null);
  const [settingHealthTarget, setSettingHealthTarget] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    Promise.all([
      API.get('/patients'),
      API.get('/prices'),
      API.get('/clinic-settings'),
      API.get('/ai-scribe/sessions?include_all=true')
    ]).then(([p, pr, cs, sc]) => {
      setPatients(p.data);
      setPrices(pr.data);
      setClinicSettings(cs.data);
      // Filter for approved sessions and include drafts as well
      setScribeSessions(sc.data.filter(s => s.status === 'approved' || s.status === 'draft'));
    }).catch(console.error);
  }, []);
  
  // Fetch visits and kiosk data when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      API.get(`/clinical/patient/${selectedPatient}`).then(r => setVisits(r.data)).catch(console.error);
      
      // Fetch kiosk check-in data
      setLoadingKiosk(true);
      API.get(`/clinical/kiosk-data/${selectedPatient}`)
        .then(r => {
          if (r.data.has_data) {
            setKioskData(r.data);
            // Auto-populate vitals and SOAP from kiosk data
            const kd = r.data.kiosk_data;
            if (kd.vitals) {
              setVitals(prev => ({
                ...prev,
                bp_systolic: kd.vitals.bp_systolic || prev.bp_systolic,
                bp_diastolic: kd.vitals.bp_diastolic || prev.bp_diastolic,
                pulse: kd.vitals.pulse || prev.pulse,
                temperature: kd.vitals.temperature || prev.temperature,
                weight: kd.vitals.weight || prev.weight,
                spo2: kd.vitals.spo2 || prev.spo2,
              }));
            }
            // Auto-populate subjective with chief complaint
            if (kd.chief_complaint) {
              setSoap(prev => ({
                ...prev,
                subjective: kd.chief_complaint + (kd.additional_notes ? `\n\nAdditional Notes: ${kd.additional_notes}` : '') + (kd.pain_scale > 0 ? `\n\nPain Scale: ${kd.pain_scale}/10` : '')
              }));
            }
            toast.success('Kiosk check-in data loaded');
          } else {
            setKioskData(null);
          }
        })
        .catch(console.error)
        .finally(() => setLoadingKiosk(false));
    } else {
      setKioskData(null);
    }
  }, [selectedPatient]);

  // Check drug interactions when medications change
  useEffect(() => {
    const medNames = medications.filter(m => m.name).map(m => m.name);
    if (medNames.length < 2) {
      setDrugWarnings([]);
      return;
    }
    const patient = patients.find(p => p.id === selectedPatient);
    const allergies = patient?.allergies || [];
    
    const debounce = setTimeout(async () => {
      setCheckingInteractions(true);
      try {
        const res = await API.post('/drug-interactions/check', { medications: medNames, patient_allergies: allergies });
        setDrugWarnings([...res.data.interactions, ...res.data.allergy_warnings]);
      } catch (err) { console.error(err); }
      finally { setCheckingInteractions(false); }
    }, 500);
    
    return () => clearTimeout(debounce);
  }, [medications, selectedPatient, patients]);

  const addDiagnosis = () => {
    if (diagInput.trim() && !diagnosis.includes(diagInput.trim())) { setDiagnosis([...diagnosis, diagInput.trim()]); setDiagInput(''); }
  };
  
  // Medication handlers
  const addMedication = () => { setMedications([...medications, { name: '', dosage: '', frequency: '', duration: '', notes: '', price: 0 }]); };
  const updateMed = (i, field, value) => { 
    const u = [...medications]; 
    u[i] = { ...u[i], [field]: value }; 
    setMedications(u);
    
    if (field === 'name' && value.length >= 2) {
      const matches = prices.filter(p => p.category === 'medicine' && p.name.toLowerCase().includes(value.toLowerCase())).slice(0, 5);
      setMedSuggestions({ ...medSuggestions, [i]: matches });
    } else if (field === 'name') {
      setMedSuggestions({ ...medSuggestions, [i]: [] });
    }
  };
  const selectMedSuggestion = (idx, price) => {
    const u = [...medications];
    u[idx] = { ...u[idx], name: price.name, price: price.price };
    setMedications(u);
    setMedSuggestions({ ...medSuggestions, [idx]: [] });
  };

  // Lab test handlers
  const addLabTest = () => { setLabTests([...labTests, { name: '', type: 'blood', notes: '', price: 0 }]); };
  const updateLabTest = (i, field, value) => {
    const u = [...labTests];
    u[i] = { ...u[i], [field]: value };
    setLabTests(u);
    
    if (field === 'name' && value.length >= 2) {
      const matches = prices.filter(p => p.category === 'test' && p.name.toLowerCase().includes(value.toLowerCase())).slice(0, 5);
      setTestSuggestions({ ...testSuggestions, [i]: matches });
    } else if (field === 'name') {
      setTestSuggestions({ ...testSuggestions, [i]: [] });
    }
  };
  const selectTestSuggestion = (idx, price) => {
    const u = [...labTests];
    u[idx] = { ...u[idx], name: price.name, price: price.price };
    setLabTests(u);
    setTestSuggestions({ ...testSuggestions, [idx]: [] });
  };

  // Radiology handlers
  const addRadiology = () => { setRadiology([...radiology, { name: '', type: 'xray', notes: '', price: 0 }]); };
  const updateRadiology = (i, field, value) => {
    const u = [...radiology];
    u[i] = { ...u[i], [field]: value };
    setRadiology(u);
    
    if (field === 'name' && value.length >= 2) {
      const matches = prices.filter(p => (p.category === 'radiology' || p.category === 'test') && p.name.toLowerCase().includes(value.toLowerCase())).slice(0, 5);
      setRadSuggestions({ ...radSuggestions, [i]: matches });
    } else if (field === 'name') {
      setRadSuggestions({ ...radSuggestions, [i]: [] });
    }
  };
  const selectRadSuggestion = (idx, price) => {
    const u = [...radiology];
    u[idx] = { ...u[idx], name: price.name, price: price.price };
    setRadiology(u);
    setRadSuggestions({ ...radSuggestions, [idx]: [] });
  };

  // Treatment handlers
  const addTreatment = () => { setTreatments([...treatments, { name: '', type: 'treatment', notes: '', price: 0, recommended: true }]); };
  const updateTreatment = (i, field, value) => {
    const u = [...treatments];
    u[i] = { ...u[i], [field]: value };
    setTreatments(u);
    
    if (field === 'name' && value.length >= 2) {
      const matches = prices.filter(p => (p.category === 'procedure' || p.category === 'treatment') && p.name.toLowerCase().includes(value.toLowerCase())).slice(0, 5);
      setTreatSuggestions({ ...treatSuggestions, [i]: matches });
    } else if (field === 'name') {
      setTreatSuggestions({ ...treatSuggestions, [i]: [] });
    }
  };
  const selectTreatSuggestion = (idx, price) => {
    const u = [...treatments];
    u[idx] = { ...u[idx], name: price.name, price: price.price };
    setTreatments(u);
    setTreatSuggestions({ ...treatSuggestions, [idx]: [] });
  };

  // Handle clinical attachment upload
  const handleAttachmentUpload = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload/clinical-attachment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` },
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setAttachments([...attachments, data]);
      toast.success('File uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingFile(false);
    }
  };

  const removeAttachment = (fileId) => {
    setAttachments(attachments.filter(a => a.file_id !== fileId));
  };

  // Marketplace search functions
  const searchMarketplaceProducts = async (query, productType, setProducts, setLoading, setShowDropdown) => {
    if (!query || query.length < 2) {
      setProducts([]);
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    try {
      const res = await API.get(`/billing/marketplace-products?search=${encodeURIComponent(query)}&product_type=${productType}`);
      setProducts(res.data.products || []);
      setShowDropdown(true);
    } catch (err) {
      console.error('Marketplace search error:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced marketplace search - Lab Tests only
  useEffect(() => {
    const timer = setTimeout(() => {
      if (marketplaceLabSearch.length >= 2) {
        searchMarketplaceProducts(marketplaceLabSearch, 'lab_test', setMarketplaceLabProducts, setSearchingLabMarketplace, setShowLabMarketplaceDropdown);
      } else {
        setMarketplaceLabProducts([]);
        setShowLabMarketplaceDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [marketplaceLabSearch]);

  // Add marketplace lab test
  const addMarketplaceLabTest = (product) => {
    setLabTests([...labTests, {
      name: product.name,
      type: product.sample_type || 'blood',
      notes: product.turnaround_time ? `TAT: ${product.turnaround_time}` : '',
      price: product.mrp || product.price || 0,
      b2b_price: product.price || 0,
      product_id: product.id,
      vendor_id: product.vendor_id,
      vendor_name: product.vendor_name
    }]);
    setMarketplaceLabSearch('');
    setShowLabMarketplaceDropdown(false);
    toast.success(`Added ${product.name} from ${product.vendor_name}`);
  };

  // Import from AI Scribe
  const handleImportScribe = async (session) => {
    setImportingScribe(true);
    try {
      const res = await API.get(`/ai-scribe/sessions/${session.id}`);
      // Handle both soap_draft and soap_note field names for backward compatibility
      const soapData = res.data.soap_draft || res.data.soap_note || {};
      setSoap({
        subjective: soapData.subjective || '',
        objective: soapData.objective || '',
        assessment: soapData.assessment || '',
        plan: soapData.plan || ''
      });
      // Also import diagnosis tags and medications if available
      if (soapData.diagnosis_tags?.length > 0) {
        setDiagnosis(soapData.diagnosis_tags);
      }
      if (soapData.medications_mentioned?.length > 0) {
        setMedications(soapData.medications_mentioned.map(m => ({
          name: m.name || '',
          dosage: m.dosage || '',
          frequency: m.frequency || '',
          duration: m.duration || '',
          notes: '',
          price: 0
        })));
      }
      if (session.patient_id) setSelectedPatient(session.patient_id);
      setScribeDialogOpen(false);
      toast.success('AI Scribe notes imported successfully');
    } catch (err) { 
      console.error('Import error:', err);
      toast.error('Failed to import notes'); 
    }
    finally { setImportingScribe(false); }
  };

  const handleSave = async () => {
    if (!selectedPatient) { toast.error('Select a patient'); return; }
    setSaving(true);
    try {
      const patient = patients.find(p => p.id === selectedPatient);
      await API.post('/clinical/visits', {
        patient_id: selectedPatient, 
        patient_name: patient?.name || '',
        vitals: { 
          bp_systolic: parseInt(vitals.bp_systolic) || 0, 
          bp_diastolic: parseInt(vitals.bp_diastolic) || 0, 
          pulse: parseInt(vitals.pulse) || 0, 
          temperature: parseFloat(vitals.temperature) || 0, 
          weight: parseFloat(vitals.weight) || 0, 
          height: parseFloat(vitals.height) || 0, 
          spo2: parseInt(vitals.spo2) || 0 
        },
        ...soap, 
        diagnosis, 
        medications: medications.filter(m => m.name),
        lab_tests: labTests.filter(t => t.name),
        radiology: radiology.filter(r => r.name),
        treatments: treatments.filter(t => t.name),
        lab_orders: labOrders, 
        follow_up_date: followUpDate,
        icd10_codes: diagnosis.map(getIcd10Code).filter(Boolean),
        attachments: attachments.map(a => ({
          file_name: a.file_name,
          file_url: a.file_url,
          file_type: a.file_type,
          description: a.description || ''
        }))
      });
      toast.success('Visit saved');
      // Reset form
      setVitals({ bp_systolic: '', bp_diastolic: '', pulse: '', temperature: '', weight: '', height: '', spo2: '' });
      setSoap({ subjective: '', objective: '', assessment: '', plan: '' });
      setDiagnosis([]); setMedications([]); setLabTests([]); setRadiology([]); setTreatments([]);
      setLabOrders(''); setFollowUpDate(''); setDrugWarnings([]); setAttachments([]);
      if (selectedPatient) { const r = await API.get(`/clinical/patient/${selectedPatient}`); setVisits(r.data); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleVoiceAppend = (field) => (text) => {
    setSoap(prev => ({ ...prev, [field]: prev[field] ? prev[field] + ' ' + text : text }));
  };

  const handlePrintRx = (visit) => {
    const patient = patients.find(p => p.id === selectedPatient);
    openPrescriptionPrint(visit, patient, clinicSettings);
  };

  const handleDownloadRx = (visit) => {
    const patient = patients.find(p => p.id === selectedPatient);
    downloadPrescriptionPdf(visit, patient, clinicSettings);
  };

  const handleWhatsAppRx = (visit) => {
    const patient = patients.find(p => p.id === selectedPatient);
    sharePrescriptionWhatsApp(visit, patient, clinicSettings);
  };

  // --- Phase 1 UX overhaul additions (non-destructive, purely additive) ---
  const applyDiagnosisMacro = (macro) => {
    if (!diagnosis.includes(macro.diagnosis)) setDiagnosis([...diagnosis, macro.diagnosis]);
    setSoap(prev => ({
      ...prev,
      assessment: prev.assessment ? `${prev.assessment} ${macro.assessment}` : macro.assessment,
      plan: prev.plan ? `${prev.plan} ${macro.plan}` : macro.plan,
    }));
    toast.success(`"${macro.label}" template applied`);
  };

  // Builds a visit-shaped object from the current in-progress encounter (unsaved) for quick actions
  const buildDraftVisit = () => ({
    patient_id: selectedPatient,
    patient_name: patients.find(p => p.id === selectedPatient)?.name || '',
    vitals: {
      bp_systolic: parseInt(vitals.bp_systolic) || 0,
      bp_diastolic: parseInt(vitals.bp_diastolic) || 0,
      pulse: parseInt(vitals.pulse) || 0,
      temperature: parseFloat(vitals.temperature) || 0,
      weight: parseFloat(vitals.weight) || 0,
      height: parseFloat(vitals.height) || 0,
      spo2: parseInt(vitals.spo2) || 0,
    },
    ...soap,
    diagnosis,
    medications: medications.filter(m => m.name),
    lab_tests: labTests.filter(t => t.name),
    radiology: radiology.filter(r => r.name),
    treatments: treatments.filter(t => t.name),
    lab_orders: labOrders,
    follow_up_date: followUpDate,
    created_at: new Date().toISOString(),
  });

  const handleQuickPrintRx = () => {
    if (!selectedPatient) { toast.error('Select a patient first'); return; }
    const patient = patients.find(p => p.id === selectedPatient);
    openPrescriptionPrint(buildDraftVisit(), patient, clinicSettings);
  };

  const handleQuickFinishWhatsApp = async () => {
    if (!selectedPatient) { toast.error('Select a patient first'); return; }
    const patient = patients.find(p => p.id === selectedPatient);
    const draft = buildDraftVisit();
    await handleSave();
    sharePrescriptionWhatsApp(draft, patient, clinicSettings);
    API.post(`/clinical/notify-prescription-whatsapp/${selectedPatient}`).catch(() => {});
  };

  const handleScheduleFollowup = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    setFollowUpDate(d.toISOString().split('T')[0]);
    toast.success(`Follow-up scheduled in ${days} days - will auto-book an appointment on save`);
  };

  const [sendingToReception, setSendingToReception] = useState(false);
  const handleSendToReceptionQueue = async () => {
    if (!selectedPatient) { toast.error('Select a patient first'); return; }
    const patient = patients.find(p => p.id === selectedPatient);
    setSendingToReception(true);
    try {
      await API.post('/billing/reception-queue', {
        patient_id: selectedPatient,
        patient_name: patient?.name || '',
        consultation_fee: clinicSettings?.consultation_fee || 500
      });
      toast.success(`${patient?.name || 'Patient'} sent to Reception Queue for billing`);
    } catch (err) {
      toast.error('Failed to send to reception queue');
    } finally {
      setSendingToReception(false);
    }
  };

  // Inline AI Scribe - record, transcribe and generate SOAP notes directly inside the Consultation Hub
  const toggleScribeRecorder = () => {
    setShowScribeRecorder(v => {
      const next = !v;
      if (next) setTimeout(() => scribePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      return next;
    });
  };

  const startScribeRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      scribeMediaRecorderRef.current = recorder;
      scribeChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) scribeChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(scribeChunksRef.current, { type: 'audio/webm' });
        setScribeTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'recording.webm');
          const res = await API.post('/ai-scribe/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          setScribeTranscription(prev => prev ? `${prev}\n${res.data.transcription}` : res.data.transcription);
          toast.success('Transcription complete');
        } catch (err) { toast.error('Transcription failed'); }
        finally { setScribeTranscribing(false); }
      };
      recorder.start(1000);
      setScribeRecording(true);
    } catch (err) { toast.error('Microphone access denied'); }
  };

  const stopScribeRecording = () => {
    if (scribeMediaRecorderRef.current && scribeRecording) {
      scribeMediaRecorderRef.current.stop();
      setScribeRecording(false);
    }
  };

  const generateAndFillFromScribe = async () => {
    if (!scribeTranscription.trim()) { toast.error('Record or type something first'); return; }
    if (!selectedPatient) { toast.error('Select a patient first'); return; }
    setScribeGenerating(true);
    try {
      const patient = patients.find(p => p.id === selectedPatient);
      const res = await API.post('/ai-scribe/generate-soap', {
        transcription: scribeTranscription,
        patient_id: selectedPatient,
        patient_name: patient?.name || '',
      });
      const draft = res.data.soap_draft || {};
      setSoap(prev => ({
        subjective: draft.subjective ? (prev.subjective ? `${prev.subjective} ${draft.subjective}` : draft.subjective) : prev.subjective,
        objective: draft.objective ? (prev.objective ? `${prev.objective} ${draft.objective}` : draft.objective) : prev.objective,
        assessment: draft.assessment ? (prev.assessment ? `${prev.assessment} ${draft.assessment}` : draft.assessment) : prev.assessment,
        plan: draft.plan ? (prev.plan ? `${prev.plan} ${draft.plan}` : draft.plan) : prev.plan,
      }));
      if (draft.diagnosis_tags?.length > 0) {
        setDiagnosis(prevD => [...new Set([...prevD, ...draft.diagnosis_tags])]);
      }
      if (draft.medications_mentioned?.length > 0) {
        setMedications(prevM => [...prevM, ...draft.medications_mentioned.map(m => ({
          name: m.name || '', dosage: m.dosage || '', frequency: m.frequency || '', duration: m.duration || '', notes: '', price: 0
        }))]);
      }
      if (res.data.warning) toast.warning(res.data.warning);
      else toast.success('AI notes added to SOAP - review before saving');
      setShowScribeRecorder(false);
      setScribeTranscription('');
      setActiveClinicalTab('soap');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setScribeGenerating(false); }
  };

  // 1-click Set Health Target - auto-suggests targets from the current diagnosis/plan, shown for review before saving
  const [showHealthTargetDialog, setShowHealthTargetDialog] = useState(false);
  const [healthTargetDraft, setHealthTargetDraft] = useState(null);

  const openHealthTargetDialog = () => {
    if (!selectedPatient) { toast.error('Select a patient first'); return; }
    const targets = { daily_water_glasses: 8, sleep_hours: 8, daily_steps: 8000, daily_calories_intake: '' };
    const restrictionsList = [];
    diagnosis.forEach(d => {
      const rule = HEALTH_TARGET_RULES[d];
      if (rule) {
        Object.assign(targets, rule);
        if (rule.restrictions) restrictionsList.push(rule.restrictions);
      }
    });
    targets.restrictions = restrictionsList.length > 0 ? restrictionsList.join('; ') : '';
    targets.notes = soap.plan ? `From consultation plan: ${soap.plan}`.slice(0, 300) : '';
    setHealthTargetDraft(targets);
    setShowHealthTargetDialog(true);
  };

  const handleSetHealthTarget = async () => {
    setSettingHealthTarget(true);
    try {
      await API.post('/health-tracking/recommendations', { patient_id: selectedPatient, ...healthTargetDraft });
      toast.success('Health targets set for patient');
      setShowHealthTargetDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set health targets');
    } finally { setSettingHealthTarget(false); }
  };

  // Vitals-anomaly flag for the "Need Attention" alert badge
  const vitalsNeedAttention = (() => {
    const sys = parseInt(vitals.bp_systolic), dia = parseInt(vitals.bp_diastolic);
    const temp = parseFloat(vitals.temperature), spo2 = parseInt(vitals.spo2), pulse = parseInt(vitals.pulse);
    const reasons = [];
    if (sys >= 140 || dia >= 90) reasons.push('Elevated BP');
    if (temp >= 100.4) reasons.push('Fever');
    if (spo2 > 0 && spo2 < 94) reasons.push('Low SpO2');
    if (pulse > 110 || (pulse > 0 && pulse < 50)) reasons.push('Abnormal Pulse');
    return reasons;
  })();

  const recentVisitsSnapshot = visits.slice(0, 3);
  // --- end Phase 1 additions ---

  return (
    <div data-testid="clinical-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{fontFamily:'Manrope'}}>Clinical Visit</h1>
          <p className="text-sm text-slate-500">Record patient consultation details</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={scribeDialogOpen} onOpenChange={setScribeDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="h-11" data-testid="import-scribe-btn">
                <FileText className="w-4 h-4 mr-2" /> Import AI Scribe
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle style={{fontFamily:'Manrope'}}>Import from AI Scribe</DialogTitle></DialogHeader>
              <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="scribe-sessions-list">
                {scribeSessions.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No AI Scribe sessions available</p>
                ) : scribeSessions.map((s, i) => (
                  <div key={s.id} className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => handleImportScribe(s)} data-testid={`scribe-session-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{s.patient_name || 'Unknown Patient'}</span>
                      <Badge className={`text-[10px] ${s.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                        {s.status === 'approved' ? 'Approved' : 'Draft'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{new Date(s.created_at).toLocaleDateString('en-IN')}</p>
                    {s.soap_draft?.assessment && <p className="text-xs text-slate-500 mt-1 truncate">{s.soap_draft.assessment}</p>}
                  </div>
                ))}
              </div>
              {importingScribe && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-teal-700" /></div>}
            </DialogContent>
          </Dialog>
          <Button className="bg-teal-700 hover:bg-teal-800 text-white h-11" onClick={handleSave} disabled={saving} data-testid="clinical-save-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Visit'}
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 dark:border-slate-800">
        <CardContent className="p-4">
          <Label className="mb-2 block">Select Patient</Label>
          <Select value={selectedPatient} onValueChange={setSelectedPatient}>
            <SelectTrigger className="h-12" data-testid="clinical-patient-select"><SelectValue placeholder="Choose a patient..." /></SelectTrigger>
            <SelectContent>
              {patients.map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.age ? `(${p.age}y)` : ''} {p.phone ? `- ${p.phone}` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* 3-column Clinical Hub: Left (25%) Vitals/Alerts/History | Center (50%) Encounter Desk | Right (25%) Quick Actions */}
      <div className="flex flex-col lg:flex-row gap-4" data-testid="clinical-hub-3-column">
        {/* LEFT COLUMN - Face Vitals logs, alert flags, historical encounter summaries */}
        <div className="w-full lg:w-1/4 space-y-3" data-testid="clinical-hub-left-column">
          {vitalsNeedAttention.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="vitals-need-attention-badge">
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-500 text-white text-[10px]">Need Attention</Badge>
              </div>
              <ul className="mt-1.5 text-xs text-amber-800 space-y-0.5">
                {vitalsNeedAttention.map(r => <li key={r}>⚠ {r}</li>)}
              </ul>
            </div>
          )}

          {/* Drug Interaction Warnings */}
          {drugWarnings.length > 0 && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-900/10">
              <CardContent className="p-3" data-testid="drug-warnings">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-red-800 text-xs mb-1.5">Drug Interaction / Allergy Warnings</p>
                    <div className="space-y-1.5">
                      {drugWarnings.map((w, i) => (
                        <div key={`el-${i}`} className="text-xs text-red-700">
                          <Badge className={`text-[9px] mr-1.5 ${w.severity === 'high' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                            {w.severity}
                          </Badge>
                          {w.drug_a && w.drug_b ? `${w.drug_a} + ${w.drug_b}: ` : ''}
                          {w.description}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Kiosk Check-in Data Loaded Indicator */}
          {kioskData && (
            <Card className="border-teal-200 bg-teal-50 dark:bg-teal-900/10">
              <CardContent className="p-3" data-testid="kiosk-data-indicator">
                <div className="flex items-start gap-2">
                  <ClipboardList className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-teal-800 text-xs">Patient Self Check-In Data Loaded</p>
                    <p className="text-[11px] text-teal-600 mt-0.5">
                      Vitals & chief complaint auto-populated.
                      {kioskData.appointment_date && ` (${kioskData.appointment_date})`}
                    </p>
                  </div>
                  {loadingKiosk && <Loader2 className="w-3.5 h-3.5 animate-spin text-teal-600" />}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Historical Encounter Summaries (compact) */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2 pt-3 px-3"><CardTitle className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent Encounters</CardTitle></CardHeader>
            <CardContent className="px-3 pb-3 pt-0 space-y-2" data-testid="recent-visits-snapshot">
              {!selectedPatient ? (
                <p className="text-xs text-slate-400 py-2">Select a patient to see history</p>
              ) : recentVisitsSnapshot.length === 0 ? (
                <p className="text-xs text-slate-400 py-2">No previous visits</p>
              ) : recentVisitsSnapshot.map((v, i) => (
                <div key={v.id} className="p-2 border border-slate-100 dark:border-slate-800 rounded-lg text-xs" data-testid={`recent-visit-snapshot-${i}`}>
                  <p className="text-[10px] text-slate-400">{new Date(v.created_at).toLocaleDateString('en-IN')}</p>
                  {v.diagnosis?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{v.diagnosis.map(d => <Badge key={d} variant="secondary" className="text-[9px]">{d}</Badge>)}</div>}
                  {v.assessment && <p className="text-slate-600 mt-1 line-clamp-2">{v.assessment}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* CENTER COLUMN - Encounter Desk */}
        <div className="w-full lg:w-1/2 space-y-4" data-testid="clinical-hub-center-column">
          {/* Inline AI Scribe (desktop: anchored top of center column) - records, transcribes & fills SOAP directly, no page redirect */}
          <button
            type="button"
            onClick={toggleScribeRecorder}
            data-testid="ai-scribe-mic-button"
            className="hidden lg:flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-3 font-semibold shadow-md hover:shadow-lg hover:brightness-105 transition-all"
            title="Record this encounter with AI Scribe"
          >
            <Mic className="w-5 h-5" /> {showScribeRecorder ? 'Close AI Scribe' : 'AI Scribe - Dictate Encounter'}
          </button>

          {showScribeRecorder && (
            <Card ref={scribePanelRef} className="border-teal-200 bg-teal-50/50 dark:bg-teal-900/10" data-testid="inline-ai-scribe-panel">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={scribeRecording ? stopScribeRecording : startScribeRecording}
                    disabled={scribeTranscribing}
                    data-testid="inline-scribe-mic-btn"
                    className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                      scribeRecording ? 'bg-red-500 hover:bg-red-600 animate-pulse shadow-lg shadow-red-500/30' : 'bg-teal-700 hover:bg-teal-800 shadow-lg shadow-teal-700/20'
                    }`}
                  >
                    {scribeRecording ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
                  </button>
                  <p className="text-xs text-slate-500 flex items-center gap-1.5">
                    {scribeTranscribing && <Loader2 className="w-3.5 h-3.5 animate-spin text-teal-700" />}
                    {scribeRecording ? 'Recording... tap mic to stop' : scribeTranscribing ? 'Transcribing...' : 'Tap mic to dictate the consultation'}
                  </p>
                </div>
                <Textarea
                  data-testid="inline-scribe-transcription"
                  value={scribeTranscription}
                  onChange={e => setScribeTranscription(e.target.value)}
                  placeholder="Transcription appears here after recording... you can also type or paste directly"
                  className="min-h-[90px] text-sm bg-white dark:bg-slate-900"
                />
                <Button
                  className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={generateAndFillFromScribe}
                  disabled={!scribeTranscription.trim() || scribeGenerating || !selectedPatient}
                  data-testid="inline-scribe-generate-btn"
                >
                  {scribeGenerating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : 'Generate & Fill Notes'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Quick Diagnosis Macro chips */}
          <div className="flex items-center gap-2 overflow-x-auto lg:overflow-visible lg:flex-wrap pb-1" data-testid="quick-diagnosis-macro-row">
            <Zap className="w-3.5 h-3.5 text-teal-600 shrink-0" />
            {DIAGNOSIS_MACROS.map(m => (
              <button
                key={m.label}
                type="button"
                onClick={() => applyDiagnosisMacro(m)}
                data-testid={`quick-diagnosis-chip-${m.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="shrink-0 px-3 py-1.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors whitespace-nowrap"
              >
                {m.label}
              </button>
            ))}
          </div>

      <Tabs value={activeClinicalTab} onValueChange={setActiveClinicalTab} className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="vitals" data-testid="tab-vitals">Vitals</TabsTrigger>
          <TabsTrigger value="soap" data-testid="tab-soap">SOAP</TabsTrigger>
          <TabsTrigger value="rx" data-testid="tab-rx">Rx</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        {/* Vitals Tab */}
        <TabsContent value="vitals">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}><Heart className="w-5 h-5 text-red-500" /> Vitals</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="clinical-vitals-form">
                {[
                  { key: 'bp_systolic', label: 'BP Systolic (mmHg)', ph: '120' },
                  { key: 'bp_diastolic', label: 'BP Diastolic (mmHg)', ph: '80' },
                  { key: 'pulse', label: 'Pulse (bpm)', ph: '72' },
                  { key: 'temperature', label: 'Temperature (°F)', ph: '98.6', step: '0.1' },
                  { key: 'weight', label: 'Weight (kg)', ph: '70', step: '0.1' },
                  { key: 'height', label: 'Height (cm)', ph: '170' },
                  { key: 'spo2', label: 'SpO2 (%)', ph: '98' },
                ].map(v => (
                  <div key={v.key}>
                    <Label className="text-xs">{v.label}</Label>
                    <Input data-testid={`vitals-${v.key.replace('_', '-')}`} type="number" step={v.step} value={vitals[v.key]} onChange={e => setVitals({...vitals, [v.key]: e.target.value})} className="h-12 mt-1" placeholder={v.ph} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SOAP Tab */}
        <TabsContent value="soap">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}><ClipboardList className="w-5 h-5 text-teal-600" /> SOAP Notes</CardTitle></CardHeader>
            <CardContent className="space-y-4" data-testid="clinical-soap-form">
              {[
                { key: 'subjective', label: 'S - Subjective', ph: "Patient's complaints, symptoms, history..." },
                { key: 'objective', label: 'O - Objective', ph: 'Clinical findings, examination results...' },
                { key: 'assessment', label: 'A - Assessment', ph: 'Diagnosis, clinical impression...' },
                { key: 'plan', label: 'P - Plan', ph: 'Treatment plan, follow-up instructions...' },
              ].map(field => (
                <div key={field.key}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm font-semibold">{field.label}</Label>
                    <VoiceInputButton onTranscription={handleVoiceAppend(field.key)} fieldName={field.key} />
                  </div>
                  <Textarea data-testid={`soap-${field.key}`} value={soap[field.key]} onChange={e => setSoap({...soap, [field.key]: e.target.value})} placeholder={field.ph} className="min-h-[80px]" />
                </div>
              ))}
              <Separator />
              <div>
                <Label className="text-sm font-semibold">Diagnosis Tags</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input data-testid="diagnosis-input" value={diagInput} onChange={e => setDiagInput(e.target.value)} placeholder="e.g., Viral Fever" className="h-11" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDiagnosis())} />
                  <Button type="button" variant="outline" className="h-11 shrink-0" onClick={addDiagnosis} data-testid="diagnosis-add-btn"><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {diagnosis.map(d => (
                    <Badge key={d} variant="secondary" className="gap-1">
                      {d}{getIcd10Code(d) && <span className="font-mono text-[9px] text-teal-600 ml-1">[{getIcd10Code(d)}]</span>}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setDiagnosis(diagnosis.filter(x => x !== d))} />
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-sm">Follow-up Date</Label><Input data-testid="followup-date" type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} className="h-11 mt-1" /></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Medications Tab */}
        <TabsContent value="rx">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Medications</CardTitle>
                <Button variant="outline" size="sm" onClick={addMedication} data-testid="add-medication-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="medications-list">
              {checkingInteractions && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Checking interactions...</div>}
              {medications.length === 0 ? (
                <div className="text-center py-8">
                  <Stethoscope className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No medications added</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={addMedication}><Plus className="w-4 h-4 mr-1" /> Add Medication</Button>
                </div>
              ) : medications.map((med, i) => (
                <div key={`el-${i}`} className="relative">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg items-end">
                    <div className="relative">
                      <Label className="text-[10px]">Drug Name</Label>
                      <Input data-testid={`med-name-${i}`} value={med.name} onChange={e => updateMed(i, 'name', e.target.value)} className="h-10 mt-0.5" placeholder="Paracetamol" />
                      {medSuggestions[i]?.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {medSuggestions[i].map(p => (
                            <button key={p.id} type="button" onClick={() => selectMedSuggestion(i, p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex justify-between">
                              <span>{p.name}</span><span className="text-teal-600">₹{p.price}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div><Label className="text-[10px]">Dosage</Label><Input data-testid={`med-dosage-${i}`} value={med.dosage} onChange={e => updateMed(i, 'dosage', e.target.value)} className="h-10 mt-0.5" placeholder="500mg" /></div>
                    <div><Label className="text-[10px]">Frequency</Label><Input value={med.frequency} onChange={e => updateMed(i, 'frequency', e.target.value)} className="h-10 mt-0.5" placeholder="1-0-1" /></div>
                    <div><Label className="text-[10px]">Duration</Label><Input value={med.duration} onChange={e => updateMed(i, 'duration', e.target.value)} className="h-10 mt-0.5" placeholder="5 days" /></div>
                    <Button variant="ghost" size="sm" className="text-red-500 h-10" onClick={() => setMedications(medications.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
              {medications.length > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-100 flex gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700">Drug interaction checks are enabled. Type medication names to auto-suggest from price catalog.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lab Tests, Radiology, Treatments Tab */}
        <TabsContent value="orders">
          <div className="space-y-4">
            {/* Marketplace Lab Tests Search */}
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-purple-700" style={{fontFamily:'Manrope'}}><ShoppingCart className="w-4 h-4" /> Add from Marketplace (Labs)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    data-testid="marketplace-lab-search"
                    value={marketplaceLabSearch}
                    onChange={e => setMarketplaceLabSearch(e.target.value)}
                    onFocus={() => marketplaceLabProducts.length > 0 && setShowLabMarketplaceDropdown(true)}
                    placeholder="Search lab tests from vendors..."
                    className="h-10 pl-10 bg-white"
                  />
                  {searchingLabMarketplace && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-purple-500" />}
                  {showLabMarketplaceDropdown && marketplaceLabProducts.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-purple-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {marketplaceLabProducts.map(p => (
                        <button key={p.id} type="button" onClick={() => addMarketplaceLabTest(p)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 border-b border-slate-100 last:border-b-0">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-medium">{p.name}</span>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge variant="secondary" className="text-[10px]">{p.sample_type || 'Lab Test'}</Badge>
                                <span className="text-xs text-purple-600"><Store className="w-3 h-3 inline mr-1" />{p.vendor_name}</span>
                              </div>
                              {p.turnaround_time && <p className="text-[10px] text-slate-400">TAT: {p.turnaround_time}</p>}
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-purple-700">₹{p.mrp || p.price}</span>
                              <p className="text-[9px] text-slate-400">MRP</p>
                              {p.mrp > 0 && p.mrp !== p.price && (
                                <p className="text-[10px] text-teal-600">B2B ₹{p.price}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-purple-600 mt-1">Lab vendors will be notified when billing is created.</p>
              </CardContent>
            </Card>

            {/* Lab Tests */}
            <Card className="border-slate-200 dark:border-slate-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2" style={{fontFamily:'Manrope'}}><FlaskConical className="w-4 h-4 text-purple-500" /> Lab Tests</CardTitle>
                  <Button variant="outline" size="sm" onClick={addLabTest} data-testid="add-lab-test-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="lab-tests-list">
                {labTests.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No lab tests ordered</p>
                ) : labTests.map((test, i) => (
                  <div key={`el-${i}`} className="relative">
                    <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg items-end">
                      <div className="relative">
                        <Label className="text-[10px]">Test Name</Label>
                        <Input data-testid={`lab-test-name-${i}`} value={test.name} onChange={e => updateLabTest(i, 'name', e.target.value)} className="h-9 mt-0.5 text-sm" placeholder="CBC, LFT..." />
                        {test.vendor_name && (
                          <Badge variant="secondary" className="mt-1 text-[10px] bg-purple-100 text-purple-700">
                            <Store className="w-3 h-3 mr-1" />{test.vendor_name}
                          </Badge>
                        )}
                        {test.vendor_id && test.price > 0 && (
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            MRP ₹{test.price}{test.b2b_price > 0 && test.b2b_price !== test.price && <span className="text-teal-600"> · B2B ₹{test.b2b_price}</span>}
                          </p>
                        )}
                        {testSuggestions[i]?.length > 0 && (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {testSuggestions[i].map(p => (
                              <button key={p.id} type="button" onClick={() => selectTestSuggestion(i, p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between">
                                <span>{p.name}</span><span className="text-teal-600">₹{p.price}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-[10px]">Type</Label>
                        <Select value={test.type} onValueChange={v => updateLabTest(i, 'type', v)}>
                          <SelectTrigger className="h-9 mt-0.5 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="blood">Blood</SelectItem>
                            <SelectItem value="urine">Urine</SelectItem>
                            <SelectItem value="stool">Stool</SelectItem>
                            <SelectItem value="culture">Culture</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1"><Label className="text-[10px]">Notes</Label><Input value={test.notes} onChange={e => updateLabTest(i, 'notes', e.target.value)} className="h-9 mt-0.5 text-sm" placeholder="Fasting req..." /></div>
                        <Button variant="ghost" size="sm" className="text-red-500 h-9 mt-4" onClick={() => setLabTests(labTests.filter((_, j) => j !== i))}><X className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Radiology */}
            <Card className="border-slate-200 dark:border-slate-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2" style={{fontFamily:'Manrope'}}><Radiation className="w-4 h-4 text-blue-500" /> Radiology</CardTitle>
                  <Button variant="outline" size="sm" onClick={addRadiology} data-testid="add-radiology-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="radiology-list">
                {radiology.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No radiology ordered</p>
                ) : radiology.map((rad, i) => (
                  <div key={`el-${i}`} className="relative">
                    <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg items-end">
                      <div className="relative">
                        <Label className="text-[10px]">Study</Label>
                        <Input data-testid={`radiology-name-${i}`} value={rad.name} onChange={e => updateRadiology(i, 'name', e.target.value)} className="h-9 mt-0.5 text-sm" placeholder="X-ray Chest..." />
                        {radSuggestions[i]?.length > 0 && (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {radSuggestions[i].map(p => (
                              <button key={p.id} type="button" onClick={() => selectRadSuggestion(i, p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between">
                                <span>{p.name}</span><span className="text-teal-600">₹{p.price}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-[10px]">Type</Label>
                        <Select value={rad.type} onValueChange={v => updateRadiology(i, 'type', v)}>
                          <SelectTrigger className="h-9 mt-0.5 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="xray">X-Ray</SelectItem>
                            <SelectItem value="ct">CT Scan</SelectItem>
                            <SelectItem value="mri">MRI</SelectItem>
                            <SelectItem value="ultrasound">Ultrasound</SelectItem>
                            <SelectItem value="ecg">ECG</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1"><Label className="text-[10px]">Notes</Label><Input value={rad.notes} onChange={e => updateRadiology(i, 'notes', e.target.value)} className="h-9 mt-0.5 text-sm" /></div>
                        <Button variant="ghost" size="sm" className="text-red-500 h-9 mt-4" onClick={() => setRadiology(radiology.filter((_, j) => j !== i))}><X className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Treatments / Surgery */}
            <Card className="border-slate-200 dark:border-slate-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2" style={{fontFamily:'Manrope'}}><Scissors className="w-4 h-4 text-amber-500" /> Treatment / Surgery</CardTitle>
                  <Button variant="outline" size="sm" onClick={addTreatment} data-testid="add-treatment-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="treatments-list">
                {treatments.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No treatments recommended</p>
                ) : treatments.map((treat, i) => (
                  <div key={`el-${i}`} className="relative">
                    <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg items-end">
                      <div className="relative">
                        <Label className="text-[10px]">Treatment</Label>
                        <Input data-testid={`treatment-name-${i}`} value={treat.name} onChange={e => updateTreatment(i, 'name', e.target.value)} className="h-9 mt-0.5 text-sm" placeholder="Minor surgery..." />
                        {treatSuggestions[i]?.length > 0 && (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {treatSuggestions[i].map(p => (
                              <button key={p.id} type="button" onClick={() => selectTreatSuggestion(i, p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between">
                                <span>{p.name}</span><span className="text-teal-600">₹{p.price}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-[10px]">Type</Label>
                        <Select value={treat.type} onValueChange={v => updateTreatment(i, 'type', v)}>
                          <SelectTrigger className="h-9 mt-0.5 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="treatment">Treatment</SelectItem>
                            <SelectItem value="minor_surgery">Minor Surgery</SelectItem>
                            <SelectItem value="major_surgery">Major Surgery</SelectItem>
                            <SelectItem value="procedure">Procedure</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1"><Label className="text-[10px]">Notes</Label><Input value={treat.notes} onChange={e => updateTreatment(i, 'notes', e.target.value)} className="h-9 mt-0.5 text-sm" /></div>
                        <Button variant="ghost" size="sm" className="text-red-500 h-9 mt-4" onClick={() => setTreatments(treatments.filter((_, j) => j !== i))}><X className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Attachments / File Upload */}
            <Card className="border-slate-200 dark:border-slate-800 border-blue-200 bg-blue-50/30">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-blue-700" style={{fontFamily:'Manrope'}}><Upload className="w-4 h-4" /> Attachments</CardTitle>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="clinical-attachment"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
                      onChange={(e) => handleAttachmentUpload(e.target.files?.[0])}
                      disabled={uploadingFile}
                    />
                    <label htmlFor="clinical-attachment">
                      <Button variant="outline" size="sm" asChild disabled={uploadingFile} data-testid="add-attachment-btn">
                        <span className="cursor-pointer">
                          {uploadingFile ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                          {uploadingFile ? 'Uploading...' : 'Add File'}
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              </CardHeader>
              <CardContent data-testid="attachments-list">
                {attachments.length === 0 ? (
                  <div className="text-center py-6">
                    <File className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">No attachments</p>
                    <p className="text-xs text-slate-400 mt-1">Upload reports, X-rays, or images</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {attachments.map((file) => (
                      <div key={file.file_id} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200">
                        {file.file_type?.startsWith('image/') ? (
                          <Image className="w-8 h-8 text-blue-500 shrink-0" />
                        ) : (
                          <FileText className="w-8 h-8 text-red-500 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.file_name}</p>
                          <p className="text-xs text-slate-400">{file.file_type?.split('/')[1]?.toUpperCase()}</p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 h-8 w-8 p-0" onClick={() => removeAttachment(file.file_id)} data-testid={`remove-attachment-${file.file_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3"><CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Visit History</CardTitle></CardHeader>
            <CardContent className="space-y-3" data-testid="visit-history">
              {!selectedPatient ? <p className="text-sm text-slate-400 text-center py-8">Select a patient to view history</p> :
               visits.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No previous visits</p> :
               visits.map((v, i) => (
                <div key={v.id} className="p-4 border border-slate-100 dark:border-slate-800 rounded-lg" data-testid={`history-item-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400">{new Date(v.created_at).toLocaleDateString('en-IN')}</span>
                    <div className="flex items-center gap-2">
                      {v.diagnosis?.length > 0 && <div className="flex gap-1">{v.diagnosis.map(d => <Badge key={d} variant="secondary" className="text-[10px]">{d}</Badge>)}</div>}
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-green-600" onClick={() => handleWhatsAppRx(v)} data-testid={`whatsapp-rx-${i}`} title="Send to WhatsApp"><MessageCircle className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-teal-600" onClick={() => handleDownloadRx(v)} data-testid={`download-rx-${i}`} title="Download PDF"><Download className="w-3 h-3 mr-1" /> Download</Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={() => handlePrintRx(v)} data-testid={`print-rx-${i}`} title="Print"><Printer className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  {v.subjective && <p className="text-sm text-slate-600"><strong>S:</strong> {v.subjective}</p>}
                  {v.assessment && <p className="text-sm text-slate-600 mt-1"><strong>A:</strong> {v.assessment}</p>}
                  {v.medications?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{v.medications.map((m, j) => <Badge key={j} variant="outline" className="text-[10px]">{m.name} {m.dosage}</Badge>)}</div>}
                  {v.lab_tests?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{v.lab_tests.map((t, j) => <Badge key={j} className="bg-purple-50 text-purple-700 text-[10px]">{t.name}</Badge>)}</div>}
                  {v.treatments?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{v.treatments.map((t, j) => <Badge key={j} className="bg-amber-50 text-amber-700 text-[10px]">{t.name}</Badge>)}</div>}
                  {v.attachments?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Upload className="w-3 h-3" /> Attachments ({v.attachments.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {v.attachments.map((att, j) => (
                          <a key={j} href={`${BACKEND_URL}${att.file_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 bg-blue-50 px-2 py-1 rounded">
                            {att.file_type?.startsWith('image/') ? <Image className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                            {att.file_name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </div>
        {/* end center column */}

        {/* RIGHT COLUMN - Quick Actions */}
        <div className="w-full lg:w-1/4 space-y-2" data-testid="clinical-hub-right-column">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2 pt-3 px-3"><CardTitle className="text-xs font-semibold uppercase tracking-wide text-slate-500">Quick Actions</CardTitle></CardHeader>
            <CardContent className="px-3 pb-3 pt-0 space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start h-11"
                onClick={handleQuickPrintRx}
                data-testid="quick-action-print-rx"
              >
                <Printer className="w-4 h-4 mr-2" /> Print Rx
              </Button>
              <Button
                className="w-full justify-start h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleQuickFinishWhatsApp}
                disabled={saving}
                data-testid="quick-action-whatsapp-send"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> Finish & Send to WhatsApp
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-11 border-teal-300 text-teal-700 hover:bg-teal-50"
                onClick={handleSendToReceptionQueue}
                disabled={sendingToReception || !selectedPatient}
                data-testid="quick-action-send-to-reception"
              >
                <Send className="w-4 h-4 mr-2" /> Send to Reception Queue
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-11"
                onClick={openHealthTargetDialog}
                disabled={!selectedPatient}
                data-testid="quick-action-set-health-target"
              >
                <Target className="w-4 h-4 mr-2" /> Set Health Target
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start h-11" data-testid="quick-action-schedule-followup">
                    <CalendarClock className="w-4 h-4 mr-2" /> Schedule Follow-Up
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <p className="text-xs text-slate-500 mb-2 px-1">Schedule follow-up in:</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {FOLLOWUP_PRESETS.map(f => (
                      <Button key={f.days} variant="outline" size="sm" className="text-xs h-8"
                        onClick={() => handleScheduleFollowup(f.days)} data-testid={`followup-preset-${f.days}`}>
                        {f.label}
                      </Button>
                    ))}
                  </div>
                  {followUpDate && <p className="text-xs text-teal-600 mt-2 px-1">Set for {followUpDate}</p>}
                </PopoverContent>
              </Popover>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* end 3-column clinical hub */}

      {/* Mobile-only fixed floating AI Scribe mic (bottom-right, thumb-accessible) */}
      <div className="lg:hidden fixed bottom-20 right-4 z-50 w-14 h-14">
        <span className="absolute inset-0 rounded-full bg-teal-500 animate-pulse-ring" aria-hidden="true" />
        <button
          type="button"
          onClick={toggleScribeRecorder}
          data-testid="ai-scribe-mic-button-mobile"
          className="relative w-14 h-14 rounded-full bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-xl flex items-center justify-center"
          title="AI Scribe"
        >
          <Mic className="w-6 h-6" />
        </button>
      </div>

      {/* Health Target Review Dialog - shows auto-filled targets before saving */}
      <Dialog open={showHealthTargetDialog} onOpenChange={setShowHealthTargetDialog}>
        <DialogContent className="max-w-md" data-testid="health-target-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Target className="w-5 h-5 text-teal-600" /> Set Health Target
            </DialogTitle>
          </DialogHeader>
          {healthTargetDraft && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Auto-suggested from the diagnosis & plan - review and edit before confirming.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Daily Water (glasses)</Label>
                  <Input type="number" value={healthTargetDraft.daily_water_glasses}
                    onChange={e => setHealthTargetDraft({ ...healthTargetDraft, daily_water_glasses: parseInt(e.target.value) || 0 })}
                    className="mt-1 h-9" data-testid="health-target-water-input" />
                </div>
                <div>
                  <Label className="text-xs">Sleep (hours)</Label>
                  <Input type="number" value={healthTargetDraft.sleep_hours}
                    onChange={e => setHealthTargetDraft({ ...healthTargetDraft, sleep_hours: parseFloat(e.target.value) || 0 })}
                    className="mt-1 h-9" data-testid="health-target-sleep-input" />
                </div>
                <div>
                  <Label className="text-xs">Daily Steps</Label>
                  <Input type="number" value={healthTargetDraft.daily_steps}
                    onChange={e => setHealthTargetDraft({ ...healthTargetDraft, daily_steps: parseInt(e.target.value) || 0 })}
                    className="mt-1 h-9" data-testid="health-target-steps-input" />
                </div>
                <div>
                  <Label className="text-xs">Daily Calories</Label>
                  <Input type="number" value={healthTargetDraft.daily_calories_intake}
                    onChange={e => setHealthTargetDraft({ ...healthTargetDraft, daily_calories_intake: parseInt(e.target.value) || '' })}
                    placeholder="optional" className="mt-1 h-9" data-testid="health-target-calories-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Dietary Restrictions</Label>
                <Input value={healthTargetDraft.restrictions}
                  onChange={e => setHealthTargetDraft({ ...healthTargetDraft, restrictions: e.target.value })}
                  placeholder="e.g. Low sodium diet" className="mt-1 h-9" data-testid="health-target-restrictions-input" />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={healthTargetDraft.notes}
                  onChange={e => setHealthTargetDraft({ ...healthTargetDraft, notes: e.target.value })}
                  className="mt-1 text-sm" rows={2} data-testid="health-target-notes-input" />
              </div>
              <Button className="w-full h-10 bg-teal-700 hover:bg-teal-800 text-white" onClick={handleSetHealthTarget}
                disabled={settingHealthTarget} data-testid="health-target-confirm-btn">
                {settingHealthTarget ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Target className="w-4 h-4 mr-2" />}
                Confirm & Save Targets
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
